import React, {
  ChangeEvent,
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  ReactElement,
  useState
} from 'react'
import { StandardTextFieldProps } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useUI } from '../hooks/useUI'
import { useCompanySpaces } from '../queries/useCompanyQueries'
import { adjustColor } from '../theme/ajustColor'

interface validationTypes {
  validate: () => boolean
  msg: string
}

interface FancyInputProps extends InputHTMLAttributes<HTMLInputElement> {
  validation?: validationTypes[]
  onlyNumber?: boolean
  label?: string
  name?: string
  value?: string | number
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  type?: HTMLInputTypeAttribute
  className?: string
  required?: boolean
  inputProps?: { maxLength?: number; pattern?: string }
  error?: boolean
  helperText?: string | null
  autoComplete?: string
  multiline?: boolean
  rows?: number
  sx?: Record<string, any>
  fullWidth?: boolean
}

const FancyInput = ({
  value,
  className,
  required,
  validation,
  type,
  onChange,
  onlyNumber,
  inputProps,
  label,
  multiline,
  rows = 4,
  fullWidth = true, // Por defecto true ya que siempre usamos w-full
  ...rest
}: FancyInputProps): ReactElement => {
  const { darkMode } = useUI()
  const { data: companyData } = useCompanySpaces()

  // Generar paleta de colores basada en el modo
  const primaryColor = companyData?.body?.content[0]?.color || '#0A3A7A'
  const colors = adjustColor(primaryColor, darkMode ? 'dark' : 'light')

  const { t } = useTranslation()
  const [touched, setTouched] = useState(false)

  const isNumber = () => (type === 'number' || onlyNumber ? onlyNumber : false)

  const isEmptyString = (str: string) => {
    return str === ''
  }

  const stringIsOnlyDigits = (str: string) => {
    return /^[0-9]+$/.test(str)
  }

  const onChangeCustom = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTouched(true)
    if (isNumber() && !isEmptyString(e.target.value) && !stringIsOnlyDigits(e.target.value)) return
    if (onChange) onChange(e)
  }

  const getMessageError = () => {
    if (validation) {
      for (const v of validation) {
        if (!v.validate()) return v.msg
      }
      return ''
    }
  }

  const requiredCondition = touched && !value && required
  const showCustomError = touched && validation && !!getMessageError()
  const hasError = requiredCondition || showCustomError

  return (
    <>
      {
        <div className={`${className || ''}`}>
          <label style={{ color: colors?.text }} className="block text-sm font-medium mb-1">
            {label}
          </label>
          {multiline ? (
            <textarea
              rows={rows}
              value={value ?? ''}
              onChange={onChangeCustom}
              style={{
                backgroundColor: colors?.background,
                borderColor: darkMode ? colors?.dark : colors?.light,
                color: colors?.text
              }}
              className={`w-full px-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                hasError ? 'border-red-500' : ''
              }`}
              {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            />
          ) : (
            <input
              type={type || 'text'}
              value={value ?? ''}
              onChange={onChangeCustom}
              style={{
                backgroundColor: colors?.background,
                borderColor: colors.dark,
                color: colors?.text
              }}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                hasError ? 'border-red-500' : ''
              } ${className || ''}`}
              {...inputProps}
              {...rest}
            />
          )}
          {hasError && (
            <p className="mt-1 text-sm text-red-600">
              {requiredCondition ? t('requiredField') : getMessageError()}
            </p>
          )}
        </div>
      }
    </>
  )
}

export default FancyInput
